import { useEffect, useState } from "react";
import { useChatSocket } from "../../context/ChatSocketContext";

export type PresenceStatus = "online" | "away" | "offline";

/**
 * Abonniert die Presence der übergebenen User-IDs und hält ihren Status aktuell.
 * Re-subscribed, wenn sich die ID-Menge ändert; lauscht auf `presence:update`.
 */
export function usePresence(userIds: string[]): Record<string, PresenceStatus> {
  const { on, emitAck, connected } = useChatSocket();
  const [statuses, setStatuses] = useState<Record<string, PresenceStatus>>({});
  const key = Array.from(new Set(userIds.filter(Boolean))).sort().join(",");

  useEffect(() => {
    const ids = key ? key.split(",") : [];
    if (!ids.length) {
      setStatuses({});
      return;
    }
    let cancelled = false;
    emitAck<{ statuses: Array<{ userId: string; status: PresenceStatus }> }>("presence:subscribe", {
      userIds: ids,
    })
      .then((res) => {
        if (cancelled || !res?.statuses) return;
        const next: Record<string, PresenceStatus> = {};
        for (const s of res.statuses) next[s.userId] = s.status;
        setStatuses(next);
      })
      .catch(() => undefined);

    const off = on("presence:update", (p: { userId: string; status: PresenceStatus }) => {
      if (ids.includes(p.userId)) {
        setStatuses((prev) => ({ ...prev, [p.userId]: p.status }));
      }
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [key, on, emitAck, connected]);

  return statuses;
}
